import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from 'ui/shadcn/dialog';
import { cn } from 'lib/utils';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'small' | 'medium' | 'large' | 'full';
    preventBackdropClose?: boolean;
    contentStyle?: React.CSSProperties;
}

const sizeMap: Record<NonNullable<ModalProps['size']>, string> = {
    small: 'max-w-md',
    medium: 'max-w-lg',
    large: 'max-w-3xl',
    full: 'max-w-[calc(100vw-2rem)] h-[calc(100vh-4rem)]',
};

/**
 * 기존 ui/Modal API를 보존하는 호환 래퍼. 내부는 Radix Dialog.
 * preventBackdropClose=true이면 외부 클릭/ESC 모두 닫기 차단.
 */
const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'medium',
    preventBackdropClose = false,
    contentStyle,
}) => {
    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open && !preventBackdropClose) {
                    onClose();
                }
            }}
        >
            <DialogContent
                className={cn('flex flex-col p-0 gap-0', sizeMap[size])}
                style={contentStyle}
                onPointerDownOutside={(e) => {
                    if (preventBackdropClose) e.preventDefault();
                }}
                onEscapeKeyDown={(e) => {
                    if (preventBackdropClose) e.preventDefault();
                }}
            >
                <DialogHeader className="px-5 py-4 border-b border-border">
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <div className={cn('px-5 py-4 overflow-y-auto', size === 'full' && 'flex-1')}>
                    {children}
                </div>
                {footer && (
                    <DialogFooter className="px-5 py-3 border-t border-border bg-neutral-50">
                        {footer}
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default Modal;
